<template>
  <VueFinalModal
    :modelValue="modelValue"
    :clickToClose="!inviando"
    :escToClose="!inviando"
    classes="documents-modal"
    content-class="documents-modal-content invoice-modal-content"
    @update:modelValue="chiudi"
  >
    <template #default>
      <button
        type="button"
        class="modal__close icons-button"
        :disabled="inviando"
        aria-label="Chiudi"
        @click="chiudi(false)"
      >
        <i class="fa-solid fa-xmark"></i>
      </button>
      <h3 class="modal__title">Emetti fattura</h3>

      <p v-if="caricamento" class="invoice-note">Carico l'anteprima…</p>

      <template v-else-if="anteprima">
        <section class="invoice-section">
          <h4>Destinatario</h4>
          <p class="invoice-recipient">
            <strong>{{ anteprima.cliente.nome }}</strong>
            <span v-if="idFiscale"> · {{ idFiscale }}</span>
            <span v-if="recapito"> · {{ recapito }}</span>
          </p>
          <div class="invoice-grid">
            <div class="field invoice-grid__wide">
              <label for="inv-street">Indirizzo</label>
              <input
                id="inv-street"
                v-model="indirizzo.street"
                type="text"
                :disabled="bloccata"
              />
            </div>
            <div class="field">
              <label for="inv-zip">CAP</label>
              <input
                id="inv-zip"
                v-model="indirizzo.zip"
                type="text"
                :disabled="bloccata"
              />
            </div>
            <div class="field">
              <label for="inv-city">Comune</label>
              <input
                id="inv-city"
                v-model="indirizzo.city"
                type="text"
                :disabled="bloccata"
              />
            </div>
            <div class="field">
              <label for="inv-prov">Provincia</label>
              <input
                id="inv-prov"
                v-model="indirizzo.province"
                type="text"
                maxlength="2"
                :disabled="bloccata"
              />
            </div>
            <div class="field">
              <label for="inv-country">Nazione</label>
              <input
                id="inv-country"
                v-model="indirizzo.country"
                type="text"
                maxlength="2"
                :disabled="bloccata"
              />
            </div>
          </div>
        </section>

        <section class="invoice-section">
          <h4>Documento</h4>
          <p class="invoice-note">
            Data: oggi, {{ oggiEstesa }} · sarà il {{ anteprima.numero }}
          </p>
        </section>

        <section class="invoice-section">
          <h4>Pagamento del saldo</h4>
          <div class="invoice-grid">
            <div class="field">
              <label>Modalità</label>
              <StatusSelect
                v-model="modalita"
                :options="opzioniModalita"
                placeholder="Seleziona modalità..."
                placeholderIcon="fa-wallet"
                :disabled="bloccata"
              />
            </div>
            <div class="field">
              <label for="inv-scadenza">Scadenza</label>
              <FlatPickr
                id="inv-scadenza"
                v-model="scadenza"
                :config="configScadenza"
                :disabled="bloccata"
                placeholder="gg/mm/aaaa"
                data-test="scadenza"
              />
              <div class="invoice-days">
                <button
                  v-for="g in [30, 60, 90]"
                  :key="g"
                  type="button"
                  class="invoice-days__btn"
                  :class="{ 'is-active': scadenza === scadenzaDa(oggi, g) }"
                  :disabled="bloccata"
                  @click="scadenza = scadenzaDa(oggi, g)"
                >
                  {{ g }} giorni
                </button>
              </div>
            </div>
          </div>
          <p
            v-if="modalita === 'bonifico' && anteprima.pagamento.iban"
            class="invoice-note"
          >
            IBAN: {{ anteprima.pagamento.iban }}
          </p>
          <ul v-if="anteprima.acconti.length" class="invoice-list">
            <li v-for="(a, i) in anteprima.acconti" :key="i">
              Acconto incassato il {{ dataIt(a.data) }}:
              {{ euro(a.importo) }}
            </li>
          </ul>
          <p class="invoice-note">
            Saldo da pagare: {{ euro(anteprima.saldo) }}
          </p>
        </section>

        <section class="invoice-section">
          <h4>Riepilogo</h4>
          <table class="invoice-summary">
            <tbody>
              <tr
                v-for="r in anteprima.importi.riepilogo"
                :key="`${r.aliquota}-${r.natura || ''}`"
              >
                <td>{{ etichettaGruppo(r) }}</td>
                <td>imponibile {{ euro(r.imponibile) }}</td>
                <td>imposta {{ euro(r.imposta) }}</td>
              </tr>
              <tr class="invoice-summary__total">
                <td colspan="2">Totale</td>
                <td>{{ euro(anteprima.importi.totale) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-if="anteprima.ddt.length" class="invoice-note">
            DDT citati:
            {{
              anteprima.ddt
                .map((d) => `N° ${d.numero} del ${dataIt(d.data)}`)
                .join(", ")
            }}
          </p>
        </section>

        <section v-if="anteprima.serveIntento" class="invoice-section">
          <h4>Dichiarazione d'intento</h4>
          <div class="invoice-grid">
            <div class="field">
              <label for="inv-intento-prot">Protocollo</label>
              <input
                id="inv-intento-prot"
                v-model="intento.protocollo"
                type="text"
                maxlength="30"
                placeholder="12345678901234567-000001"
                :disabled="bloccata"
                data-test="intento-protocollo"
              />
              <small
                v-if="intento.protocollo && !protocolloValido"
                class="invoice-hint"
                data-test="intento-hint"
              >
                Il protocollo ha 17 cifre, un trattino e 6 cifre.
              </small>
            </div>
            <div class="field">
              <label for="inv-intento-data">Data</label>
              <FlatPickr
                id="inv-intento-data"
                v-model="intento.data"
                :config="configIntento"
                :disabled="bloccata"
                placeholder="gg/mm/aaaa"
                data-test="intento-data"
              />
            </div>
          </div>
        </section>

        <p
          v-if="anteprima.avvisoBollo"
          class="invoice-warning"
          data-test="avviso-bollo"
        >
          Possibile imposta di bollo da 2 € (righe esenti o non imponibili oltre
          77,47 €): da far confermare al commercialista. Il CRM non la aggiunge
          alla fattura.
        </p>

        <p v-if="mancanze.length && !esito" class="invoice-missing">
          Mancano: {{ mancanze.join(", ") }}.
        </p>

        <div
          v-if="esito"
          class="invoice-outcome"
          :class="'invoice-outcome--' + esito.status"
        >
          {{ testoEsito }}
        </div>

        <div class="invoice-actions">
          <button
            v-if="esito"
            type="button"
            class="invoice-actions__close"
            @click="chiudi(false)"
          >
            Chiudi
          </button>
          <button
            v-else-if="!mancanze.length"
            type="button"
            class="invoice-actions__send"
            data-test="invia"
            :disabled="inviando"
            @click="invia"
          >
            <i
              v-if="inviando"
              class="fa-solid fa-spinner fa-spin"
              aria-hidden="true"
            ></i>
            {{ inviando ? "Invio a DocuMI in corso…" : "Invia" }}
          </button>
        </div>
      </template>
    </template>
  </VueFinalModal>
</template>

<script>
import axiosInstance from "@/axiosInstance";
import StatusSelect from "@/components/StatusSelect.vue";
import FlatPickr from "vue-flatpickr-component";
import { Italian } from "flatpickr/dist/l10n/it.js";
import { NATURE } from "@/utilities/nature";
import {
  ETICHETTA_INTENTO,
  MODALITA,
  intentoValido,
  mancanzeIndirizzo,
  protocolloIntentoValido,
  messaggioEsito,
  scadenzaDa,
  senzaMancanzeIndirizzo,
} from "@/utilities/fatture";

// Il giorno di Roma, come lo calcola l'API: la data fattura è sempre oggi.
const giornoRoma = () =>
  new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Rome" }).format(
    new Date()
  );

export default {
  name: "InvoiceModal",
  components: { StatusSelect, FlatPickr },
  props: {
    quote: { type: Object, required: true },
    modelValue: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "emessa"],
  data() {
    return {
      // Lo stesso calendario del form del preventivo.
      dateConfig: {
        locale: Italian,
        dateFormat: "Y-m-d",
        altInput: true,
        altFormat: "d/m/Y",
        allowInput: true,
        monthSelectorType: "static",
      },
      caricamento: false,
      anteprima: null,
      indirizzo: { street: "", zip: "", city: "", province: "", country: "IT" },
      modalita: "bonifico",
      intento: { protocollo: "", data: "" },
      scadenza: "",
      oggi: giornoRoma(),
      inviando: false,
      esito: null,
      opzioniModalita: MODALITA,
    };
  },
  computed: {
    // La scadenza non può stare prima della data della fattura (oggi).
    configScadenza() {
      return { ...this.dateConfig, minDate: this.oggi };
    },
    // La dichiarazione d'intento precede la fattura: non oltre oggi.
    configIntento() {
      return { ...this.dateConfig, maxDate: this.oggi };
    },
    // Le mancanze d'indirizzo seguono i campi mentre si scrive; le altre sono
    // quelle dell'anteprima.
    mancanze() {
      if (!this.anteprima) return [];
      // Come l'indirizzo, anche l'intento si ricalcola sui campi.
      const intento =
        this.anteprima.serveIntento && !intentoValido(this.intento, this.oggi)
          ? [ETICHETTA_INTENTO]
          : [];
      return [
        ...senzaMancanzeIndirizzo(this.anteprima.mancanze).filter(
          (x) => x !== ETICHETTA_INTENTO
        ),
        ...mancanzeIndirizzo(this.indirizzo),
        ...intento,
      ];
    },
    protocolloValido() {
      return protocolloIntentoValido(this.intento.protocollo);
    },
    bloccata() {
      return this.inviando || Boolean(this.esito);
    },
    oggiEstesa() {
      return this.dataIt(this.oggi);
    },
    idFiscale() {
      const c = this.anteprima?.cliente;
      if (!c) return "";
      return c.vatNumber
        ? `P.IVA ${c.vatNumber}`
        : c.taxCode
        ? `CF ${c.taxCode}`
        : "";
    },
    recapito() {
      const c = this.anteprima?.cliente;
      if (!c) return "";
      if (c.codiceDestinatario && c.codiceDestinatario !== "0000000")
        return `Codice ${c.codiceDestinatario}`;
      return c.pec ? `PEC ${c.pec}` : "";
    },
    testoEsito() {
      const e = this.esito;
      if (!e) return "";
      if (e.status === "inviata")
        return `Fattura ${e.numero} inviata a DocuMI. L'esito dell'SdI arriva di solito entro un paio d'ore.`;
      if (e.status === "scartata")
        return e.statusMessage || "La fattura è stata scartata.";
      return messaggioEsito(e.status) ?? "Fattura emessa.";
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(aperta) {
        if (aperta) this.carica();
      },
    },
  },
  methods: {
    scadenzaDa,
    // Gruppo senza IVA: il codice Natura (con la descrizione), mai «IVA 0%».
    etichettaGruppo(r) {
      if (!r.natura) return `IVA ${r.aliquota}%`;
      const n = NATURE.find((x) => x.codice === r.natura);
      return n ? `${n.codice} · ${n.descrizione}` : r.natura;
    },
    dataIt(iso) {
      const [y, m, d] = String(iso).slice(0, 10).split("-");
      return `${d}/${m}/${y}`;
    },
    euro(n) {
      return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
      }).format(Number(n) || 0);
    },
    chiudi(valore) {
      if (this.inviando) return;
      this.$emit("update:modelValue", valore);
    },
    async carica() {
      this.caricamento = true;
      this.esito = null;
      this.oggi = giornoRoma();
      try {
        const { data } = await axiosInstance.get(
          `/invoices/order/${this.quote.id}/preview`
        );
        this.anteprima = data;
        this.indirizzo = {
          ...this.indirizzo,
          ...(data.cliente?.indirizzo ?? {}),
        };
        this.intento = {
          protocollo: data.cliente?.intento?.protocollo ?? "",
          data: data.cliente?.intento?.data ?? "",
        };
        this.modalita = data.pagamento?.modalita || "bonifico";
        this.scadenza = scadenzaDa(this.oggi, data.pagamento?.giorni ?? 30);
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            "Errore nel caricamento dell'anteprima della fattura."
        );
      } finally {
        this.caricamento = false;
      }
    },
    // La data non si manda: la fattura porta sempre il giorno dell'invio.
    corpo() {
      const corpo = {
        modalita: this.modalita,
        scadenza: this.scadenza,
        indirizzo: { ...this.indirizzo },
      };
      // L'API valida l'intento se c'è: lo si manda solo quando serve.
      if (this.anteprima?.serveIntento)
        corpo.intento = {
          protocollo: this.intento.protocollo.trim(),
          data: this.intento.data,
        };
      return corpo;
    },
    async invia() {
      if (this.inviando) return;
      this.inviando = true;
      try {
        const { data } = await axiosInstance.post(
          `/invoices/order/${this.quote.id}`,
          this.corpo(),
          { timeout: 70000 }
        );
        this.esito = data.invoice;
        this.$emit("emessa", data.invoice);
        if (data.invoice.status === "inviata") {
          this.$toast.success(
            `Fattura ${data.invoice.numero} inviata a DocuMI.`
          );
        }
      } catch (err) {
        if (err?.code === "ECONNABORTED" || !err?.response) {
          // Il browser ha smesso di aspettare, l'API forse no: non si propone un nuovo invio.
          this.esito = { status: "da_verificare" };
          this.$emit("emessa", null);
        } else {
          this.$toast.error(
            err.response.data?.message || "Fattura non emessa."
          );
        }
      } finally {
        this.inviando = false;
      }
    },
  },
};
</script>

<style scoped>
/* [sc-48139] Come «Gestisci pagamenti»: titolo allineato al contenuto, senza
   filetto, e niente scroll orizzontale. */
:global(.documents-modal .documents-modal-content.invoice-modal-content) {
  overflow-x: hidden;
  padding: 16px 24px 24px;
}
:global(.documents-modal-content.invoice-modal-content .modal__title) {
  padding: 0 0 4px;
  border-bottom: none;
}
.invoice-section {
  margin-top: 16px;
}
.invoice-section h4 {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 700;
}
.invoice-recipient,
.invoice-note {
  margin: 4px 0;
  font-size: 14px;
  color: #374151;
}
.invoice-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
}
.invoice-grid__wide {
  grid-column: 1 / -1;
}
.invoice-grid .field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.invoice-grid label {
  font-size: 13px;
  color: #6b7280;
}
/* :deep perché flatpickr crea a parte il campo che si vede (altInput),
   senza l'attributo dello stile scoped. */
.invoice-grid :deep(input) {
  width: 100%;
  box-sizing: border-box;
  min-height: 36px;
  padding: 0 12px;
  border: 0.5px solid #d1d5db;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
}
.invoice-days {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}
/* La tendina della piattaforma riempie la colonna come gli altri campi. */
.invoice-grid .field :deep(.status-select) {
  display: block;
  width: 100%;
}
.invoice-grid .field :deep(.status-select__trigger) {
  width: 100%;
  min-height: 36px;
  box-sizing: border-box;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 14px;
}
.invoice-days__btn {
  min-height: 32px;
  padding: 0 12px;
  border: 0.5px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
}
.invoice-days__btn.is-active {
  border-color: #111827;
  font-weight: 700;
}
.invoice-list {
  margin: 4px 0;
  padding-left: 18px;
  font-size: 14px;
}
.invoice-summary {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.invoice-summary td {
  padding: 4px 0;
}
.invoice-summary td:last-child {
  text-align: right;
}
.invoice-summary__total td {
  border-top: 0.5px solid #e5e7eb;
  font-weight: 700;
}
.invoice-missing {
  margin: 16px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 14px;
}
.invoice-hint {
  font-size: 12px;
  color: #b91c1c;
}
.invoice-warning {
  margin: 16px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff7ed;
  color: #c2410c;
  font-size: 14px;
}
.invoice-outcome {
  margin-top: 16px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 14px;
}
.invoice-outcome--da_verificare {
  background: #fff7ed;
  color: #c2410c;
}
.invoice-outcome--scartata {
  background: #fef2f2;
  color: #b91c1c;
}
.invoice-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}
.invoice-actions__send,
.invoice-actions__close {
  min-height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: 8px;
  background: #111827;
  color: #fff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.invoice-actions__send:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
@media (max-width: 560px) {
  .invoice-grid {
    grid-template-columns: 1fr;
  }
}
</style>
